'use client';

import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { FinderData } from '@/lib/types';
import { DAYS, EMPTY_FILTERS, type FilterState } from '@/lib/filters';
import { campusShort, pluralGroups, shortDay } from '@/lib/format';
import { ToggleChip } from './chips';
import { CampusIcon } from './CampusIcon';
import { SheetHeader } from './SheetHeader';

interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  options: FinderData['options'];
  filters: FilterState;
  onChange: (next: FilterState) => void;
  /** How many open groups the current filters produce, for the footer button */
  openCount: number;
  unavailableCount: number;
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h4 className="text-xs font-bold tracking-widest text-gray-500 uppercase">{title}</h4>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function toggleInList(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
}

const CITY_LIMIT = 24;

/**
 * Every filter the old finder offered, as tap-to-toggle chips. Changes apply
 * instantly (the list behind the sheet is filtered client-side), so there is
 * no Apply button, just a count to confirm and a way to start over.
 */
export function FilterSheet({
  open,
  onClose,
  options,
  filters,
  onChange,
  openCount,
  unavailableCount,
}: FilterSheetProps) {
  const [cityQuery, setCityQuery] = useState('');

  const set = <K extends keyof FilterState>(key: K, value: FilterState[K]) =>
    onChange({ ...filters, [key]: value });

  const single = <K extends 'campus' | 'lifeStage' | 'household' | 'gender'>(key: K, id: number) =>
    set(key, (filters[key] === id ? null : id) as FilterState[K]);

  const visibleCities = useMemo(() => {
    const q = cityQuery.trim().toLowerCase();
    const selected = options.cities.filter((c) => filters.cities.includes(c));
    const rest = options.cities.filter(
      (c) => !filters.cities.includes(c) && (!q || c.toLowerCase().includes(q))
    );
    return {
      selected,
      rest: rest.slice(0, CITY_LIMIT),
      hidden: Math.max(0, rest.length - CITY_LIMIT),
    };
  }, [options.cities, filters.cities, cityQuery]);

  const anySet =
    filters.campus !== null ||
    filters.lifeStage !== null ||
    filters.kids !== null ||
    filters.seeJesus ||
    filters.household !== null ||
    filters.gender !== null ||
    filters.days.length > 0 ||
    filters.cities.length > 0 ||
    filters.search.trim() !== '' ||
    filters.showFull;

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      maxWidth="max-w-2xl"
      noPanelPadding
      panelClassName="bg-white"
      header={
        <SheetHeader
          title="Filters"
          subtitle={
            unavailableCount > 0
              ? `${pluralGroups(openCount)} with room · ${unavailableCount} full`
              : `${pluralGroups(openCount)} with room`
          }
        />
      }
      footer={
        <div className="flex items-center gap-3 border-t border-gray-200 bg-white px-6 py-4 sm:px-8">
          <button
            type="button"
            disabled={!anySet}
            onClick={() => onChange({ ...EMPTY_FILTERS, groupIds: filters.groupIds })}
            className="rounded-full px-4 py-2.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex flex-1 items-center justify-center rounded-full bg-[#299c8f] px-6 py-3 text-base font-bold text-white shadow-sm transition-all hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
          >
            Show {pluralGroups(openCount + (filters.showFull ? unavailableCount : 0))}
          </button>
        </div>
      }
    >
      <SheetPage name="main">
        <div className="flex flex-col gap-7 px-6 py-6 sm:px-8">
          <Section title="See Jesus" hint="Short-term groups in the See Jesus campaign">
            <div className="flex flex-wrap gap-2">
              <ToggleChip
                active={filters.seeJesus}
                onClick={() => set('seeJesus', !filters.seeJesus)}
              >
                See Jesus Groups only
              </ToggleChip>
            </div>
          </Section>

          <Section title="Campus">
            <div className="flex flex-wrap gap-2">
              {options.campuses.map((c) => (
                <ToggleChip
                  key={c.id}
                  active={filters.campus === c.id}
                  onClick={() => single('campus', c.id)}
                >
                  <CampusIcon congregationId={c.id} className="h-4 w-4" />
                  {campusShort(c.label)}
                </ToggleChip>
              ))}
            </div>
          </Section>

          <Section title="Day of the week" hint="Pick as many as you like">
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d) => (
                <ToggleChip
                  key={d}
                  active={filters.days.includes(d)}
                  onClick={() => set('days', toggleInList(filters.days, d))}
                >
                  {shortDay(d)}
                </ToggleChip>
              ))}
            </div>
          </Section>

          <Section title="Life stage">
            <div className="flex flex-wrap gap-2">
              {options.lifeStages.map((o) => (
                <ToggleChip
                  key={o.id}
                  active={filters.lifeStage === o.id}
                  onClick={() => single('lifeStage', o.id)}
                >
                  {o.label}
                </ToggleChip>
              ))}
            </div>
          </Section>

          <Section title="Kids">
            <div className="flex flex-wrap gap-2">
              <ToggleChip
                active={filters.kids === 1}
                onClick={() => set('kids', filters.kids === 1 ? null : 1)}
              >
                Kids welcome
              </ToggleChip>
              <ToggleChip
                active={filters.kids === 0}
                onClick={() => set('kids', filters.kids === 0 ? null : 0)}
              >
                Adults only
              </ToggleChip>
            </div>
          </Section>

          <Section title="Household">
            <div className="flex flex-wrap gap-2">
              {options.households.map((o) => (
                <ToggleChip
                  key={o.id}
                  active={filters.household === o.id}
                  onClick={() => single('household', o.id)}
                >
                  {o.label}
                </ToggleChip>
              ))}
            </div>
          </Section>

          <Section title="Men's or women's">
            <div className="flex flex-wrap gap-2">
              {options.genders.map((o) => (
                <ToggleChip
                  key={o.id}
                  active={filters.gender === o.id}
                  onClick={() => single('gender', o.id)}
                >
                  {o.label}
                </ToggleChip>
              ))}
            </div>
          </Section>

          <Section title="City" hint={`${options.cities.length} cities`}>
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={cityQuery}
                onChange={(e) => setCityQuery(e.target.value)}
                placeholder="Type a city"
                className="w-full rounded-full border border-gray-200 bg-white py-2.5 pr-10 pl-10 text-sm text-[#1c2b39] placeholder:text-gray-400 focus:border-[#299c8f] focus:ring-2 focus:ring-[#299c8f]/30 focus:outline-none"
              />
              {cityQuery && (
                <button
                  type="button"
                  aria-label="Clear city search"
                  onClick={() => setCityQuery('')}
                  className="absolute top-1/2 right-2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {visibleCities.selected.map((c) => (
                <ToggleChip
                  key={c}
                  active
                  onClick={() => set('cities', toggleInList(filters.cities, c))}
                >
                  {c}
                </ToggleChip>
              ))}
              {visibleCities.rest.map((c) => (
                <ToggleChip
                  key={c}
                  active={false}
                  onClick={() => set('cities', toggleInList(filters.cities, c))}
                >
                  {c}
                </ToggleChip>
              ))}
            </div>
            {visibleCities.hidden > 0 && (
              <p className="mt-2 text-xs text-gray-400">
                {visibleCities.hidden} more. Type to narrow the list.
              </p>
            )}
            {visibleCities.rest.length === 0 && visibleCities.selected.length === 0 && (
              <p className="mt-2 text-xs text-gray-400">No city matches that.</p>
            )}
          </Section>

          <Section title="Full groups">
            <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-gray-200 px-4 py-3">
              <span className="text-sm text-[#1c2b39]">
                <span className="font-semibold">Include groups that are full</span>
                <span className="block text-xs text-gray-500">
                  {unavailableCount === 0
                    ? 'None of the matching groups are full right now'
                    : `${pluralGroups(unavailableCount)} match your filters but have no room right now`}
                </span>
              </span>
              <input
                type="checkbox"
                checked={filters.showFull}
                onChange={(e) => set('showFull', e.target.checked)}
                className="h-5 w-5 shrink-0 accent-[#299c8f]"
              />
            </label>
          </Section>
        </div>
      </SheetPage>
    </ResponsiveSheet>
  );
}
